import { FC, ReactElement, useEffect, useRef, useState } from "react";
import { fetchAsync, postAsync, postWithoutSign } from "../services/ApiHandler";
import toast, { Toaster } from "react-hot-toast";
import { CardRegistrationDialog, CardRegistrationDialogProps } from "../parts/CardRegisterDialog";
import { GenerationCreditDialog, GenerationCreditDialogProps } from "../parts/GenerationCreditDialog";
import { Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useHeaderStore } from "../parts/CommonHeader";
import { getNextPlanId, getPlanDescription } from "../services/SubscriptionService";
import { getPlanSummary } from "../services/SubscriptionService";
import { SubscriptionDialog } from "../parts/SubscriptionDialog";
import { SubscriptionDialogProps } from "../parts/SubscriptionDialog";
import { LoginDialog } from "../parts/LoginDialog";
import { Helmet } from "react-helmet";
import { getModalOffset } from "../services/ScreenUtil";
import { StripeProduct } from "../model/StripeProduct";

export const PlanPage: FC = () => {
  const [span, setSpan] = useState("month");
  const [cardProps, setCardProps] = useState<CardRegistrationDialogProps | undefined>(undefined);
  const [generationCreditDialogProps, setGenerationCreditDialogProps] = useState<GenerationCreditDialogProps | undefined>(undefined);
  const [subscriptionDialogProps, setSubscriptionDialogProps] = useState<SubscriptionDialogProps | undefined>(undefined);
  const [creditDialogOpened, { open, close }] = useDisclosure(false);
  const [subscriptionDialogOpened, { open: openSubscriptionDialog, close: closeSubscriptionDialog }] = useDisclosure(false);
  const { subscription, setSubscription, card, fetchCard, isLoggedIn, setIsLoggedIn, screenWidth } = useHeaderStore();
  const [loginDialogOpened, { open: openLoginDialog, close: closeLoginDialog }] = useDisclosure(false);
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [stripeProducts, setStripeProducts] = useState<StripeProduct[]>([]);

  useEffect(() => {
    fetchStripeProducts();
  }, []);
  
  var planTextStyleMap: { [key: string]: any; } = {
    'free': '#868E96',
    'lite': '#03034F',
    'standard': 'linear-gradient(91.51deg, #0000F3 0.07%, #F416BF 100.07%)',
  };

  async function fetchStripeProducts() {
    const list = await fetchAsync<StripeProduct[]>('Product/GetProductList');
    setStripeProducts(list.filter(product => product.productType === 'credit').sort((a, b) => a.price - b.price));
  }
  
  function buildSpanSwitch(){
    return <div className="flex justify-center mt-4 bg-gray-200 w-[168px] mx-auto rounded-lg">
      <span onClick={() => setSpan('month')} className={`my-1 rounded-md cursor-pointer w-[80px] ${span === 'month' ? 'bg-white' : ''}` }>月</span>
      <span onClick={() => setSpan('year')} className={`my-1 rounded-md cursor-pointer w-[80px] ${span === 'year' ? 'bg-white' : ''}`}>年</span>
    </div>
  }

  function onClickCreditPurchase(){
    open();
    setGenerationCreditDialogProps({
      onPurchase: () => {
        toast.success("購入しました");
        setGenerationCreditDialogProps(undefined);
      }
    } as GenerationCreditDialogProps);
  }

  function onClickPlanChange(planId: string){
    setSubscriptionDialogProps({
      planId: planId
    } as SubscriptionDialogProps);
    openSubscriptionDialog();
  }

  function buildPriceArea(monthlyPrice: number, yearlyPrice: number, planId: string, planName: string){
    var textStyle = planTextStyleMap[planId];
    return <>
      <div className="max-w-[320px] mx-auto sm:mx-2 text-left py-4 px-6 rounded-lg bg-[#F8F9FA] mb-6 sm:mb-0">
        <div className="h-[260px]">
          <div className="text-lg font-bold">
            <span style={{background: textStyle, color: "transparent", backgroundClip: "text"}}>{planName}</span>
          </div>
          { span === 'month' &&
            <div className="text-2xl font-bold">
              <span>{`¥ ${monthlyPrice.toLocaleString('ja-JP')}`}</span>
              { planId !== 'free' &&
                <span className="text-sm text-[#868E96]"> / 月</span>
              }
            </div>
          }
          { span === 'year' &&
            <div className="text-2xl font-bold">
              <span>{`¥ ${yearlyPrice.toLocaleString('ja-JP')}`}</span>
              { planId !== 'free' &&
                <span className="text-sm text-[#868E96]"> / 年</span>
              }
            </div>
          }
          <div className="mt-2 font-bold text-sm">{getPlanSummary(planId)}</div>
          <div className="mt-4 whitespace-pre-wrap text-sm">{getPlanDescription(planId)}</div>
        </div>
        { subscription === undefined &&
          <div className="h-16"></div>
        }
        { ( subscription !== undefined && (isLoggedIn === false || !(planId === 'free' && subscription?.internalProductId !== 'free'))) &&
        <div className="flex justify-center mt-4 mb-2">
          { isLoggedIn === false &&
            <div onClick={() => openLoginDialog()} className="clickable bg-[#EAEAFF] text-[#0909FF] px-4 py-2 rounded-md w-full text-center font-bold">アカウント登録</div>
          }
          { isLoggedIn === true && subscription?.internalProductId.startsWith(planId) &&  
            <div className="bg-[#EAEAFF] text-[#0909FF] px-4 py-2 rounded-md w-full text-center font-bold">現在ご利用中のプラン</div>
          }
          { isLoggedIn === true && !subscription?.internalProductId.startsWith(planId) && !subscription?.internalProductId.startsWith('standard') &&
            <button onClick={() => onClickPlanChange(planId)} className="bg-[#0909FF] text-white px-4 py-2 rounded-md w-full font-bold">このプランを選ぶ</button>
          }
        </div>
        }
      </div>
    </>
  }

  function onLogin(){
    closeLoginDialog();
    fetchCard();
    setIsLoggedIn(true);
  }

  function onClickLogin(){
    setIsLoginMode(true);
    openLoginDialog();
  }

  function onClickRegisterLink(){
    setIsLoginMode(false);
    openLoginDialog();
  }

  function onClickUpgradePlan(){
    close();
    const nextPlanId = getNextPlanId(subscription);
    setSubscriptionDialogProps({
      planId: nextPlanId
    } as SubscriptionDialogProps);
    openSubscriptionDialog();
  }

  return <>
    <Helmet>
      <title>料金プラン - Drop AI</title>
      <meta name="description" content="Drop AI の料金プランについて確認できます。" />

      <meta property="og:url" content="https://drop4i.com/plan" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="料金プラン - Drop AI | 無料で使えるAI画像生成ツール" />
      <meta property="og:description" content="Drop AI の料金プランについて確認できます。" />
    </Helmet>
    <Toaster />
    <Modal size="auto" opened={loginDialogOpened} onClose={closeLoginDialog} yOffset={getModalOffset(screenWidth)}>
      <LoginDialog onCancel={() => closeLoginDialog()} onLogin={() => onLogin()} isLogin={isLoginMode} />
    </Modal>
    { cardProps !== undefined &&
    <CardRegistrationDialog clientSecret={cardProps?.clientSecret!} onClose={() => setCardProps(undefined)} />
    }
    { generationCreditDialogProps !== undefined &&
      <Modal padding={0} size="auto" opened={creditDialogOpened} onClose={close} yOffset={getModalOffset(screenWidth)} closeButtonProps={{ className: 'mr-3' }}>
        <GenerationCreditDialog
          onPurchase={() => setGenerationCreditDialogProps(undefined)}
          onCancel={() => setGenerationCreditDialogProps(undefined)}
          onUpgrade={() => onClickUpgradePlan()}
          />
      </Modal>
    }
    { subscriptionDialogProps !== undefined &&
      <Modal size="auto" opened={subscriptionDialogOpened} onClose={closeSubscriptionDialog} yOffset={getModalOffset(screenWidth)}>
        <SubscriptionDialog onPurchase={() => setSubscriptionDialogProps(undefined)} onCancel={() => setSubscriptionDialogProps(undefined)} planId={subscriptionDialogProps?.planId} />
      </Modal>
    }
    <div className="text-center mt-12">
      <div className="text-2xl font-bold">料金プラン</div>
      { buildSpanSwitch() }
      <div className="block sm:flex justify-center mt-12">
        { buildPriceArea(0, 0, "free", "Freeプラン") }
        { buildPriceArea(780, 8424, "lite", "Liteプラン") }
        { buildPriceArea(3500, 33600, "standard", "Standardプラン") }
      </div>
      <div className="text-sm mt-8">
        <span>※ 料金プラン変更の詳細については</span>
        <a href="/article/help/about-plan" className="underline text-[#0909FF]">ヘルプページ</a>
        <span>をご確認ください。</span>
      </div>
      <div className="mt-20 text-2xl font-bold">プランの比較</div>
      { buildSpanSwitch() }
      <table className="w-[1000px] mx-auto mt-12 mb-6 hidden sm:table">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="h-12"></th>
            <th className="text-gray-500">Freeプラン</th>
            <th>Liteプラン</th>
            <th className="gradation-text">Standardプラン</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-gray-200">
            <td>価格（税込)</td>
            <td className="font-bold">¥0</td>
            <td className="py-6 font-bold">
              { span === 'year' ? <div>年間 ¥8,424</div> : <div>¥780</div> }
              { span === 'year' && <div className="text-sm text-[#868E96] font-normal">(月あたり ¥702)</div> }
            </td>
            <td className="font-bold">
              { span === 'year' ? <div>年間 ¥33,600</div> : <div>¥3,500</div> }
              { span === 'year' && <div className="text-sm text-[#868E96] font-normal">(月あたり ¥2,800)</div> }
            </td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-6">画像生成上限</td>
            <td className="font-bold">1日あたり4件</td>
            <td className="text-[#0909FF] font-bold">1ヶ月あたり 600件</td>
            <td className="text-[#0909FF] font-bold">1ヶ月あたり 3,000件</td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-4">
              <div>広告非表示</div>
              <div className="text-xs">※1</div>
            </td>
            <td><img src="/images/icon-close.svg" alt="x" className="h-[16px] mx-auto" /></td>
            <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
            <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-6">同時画像生成</td>
            <td className="font-bold">1回まで</td>
            <td className="font-bold">4回まで</td>
            <td className="font-bold">4回まで</td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-4">
              <div>生成画像の保存期限</div>
              <div className="text-xs">※2</div>
            </td>
            <td className="font-bold">30日間</td>
            <td className="font-bold">90日間</td>
            <td className="text-[#0909FF] font-bold">無制限</td>
          </tr>
          <tr>
            <td className="py-4">
              <div>画像の詳細設定</div>
              <div className="text-xs">※3</div>
            </td>
            <td><img src="/images/icon-close.svg" alt="x" className="h-[16px] mx-auto" /></td>
            <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
            <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
          </tr>
        </tbody>
      </table>

      <div className="block sm:hidden px-4 mt-10">
        <div className="flex w-full border-b border-gray-200 justify-around">
          <span className="text-gray-500 text-sm">Freeプラン</span>
          <span className="text-sm">Liteプラン</span>
          <span className="gradation-text text-sm">Standardプラン</span>
        </div>
        <div className="text-center mt-2 mb-2">価格（税込)</div>
        <div className="flex w-full border-b border-gray-200 justify-around pb-4">
          <span className="font-bold">¥0</span>
          <span className="font-bold">
            { span === 'year' ? <div>年間 ¥8,424</div> : <div>¥780</div> }
            { span === 'year' && <div className="text-sm text-[#868E96] font-normal">(月あたり ¥702)</div> }
          </span>
          <span className="font-bold">
            { span === 'year' ? <div>年間 ¥33,600</div> : <div>¥3,500</div> }
            { span === 'year' && <div className="text-sm text-[#868E96] font-normal">(月あたり ¥2,800)</div> }
          </span>
        </div>
        <div className="text-center mt-4">画像生成上限</div>
        <div className="flex w-full border-b border-gray-200 justify-around my-2 pb-4">
          <span className="font-bold">1日あたり<br/>4件</span>
          <span className="text-[#0909FF] font-bold">1ヶ月あたり<br/>600件</span>
          <span className="text-[#0909FF] font-bold">1ヶ月あたり<br/>3,000件</span>
        </div>
        <div className="text-center mb-2">広告非表示<div className="text-xs">※1</div></div>
        <div className="flex w-full border-b border-gray-200 justify-around pb-4">
          <span><img src="/images/icon-close.svg" alt="x" className="h-[16px] mx-auto" /></span>
          <span><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></span>
          <span><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></span>
        </div>
        <div className="text-center mt-4 mb-2">同時画像生成</div>
        <div className="flex w-full border-b border-gray-200 justify-around pb-4">
          <span className="font-bold">1回まで</span>
          <span className="font-bold">4回まで</span>
          <span className="font-bold">4回まで</span>
        </div>
        <div className="text-center mt-4 mb-2">生成画像の保存期限<div className="text-xs">※2</div></div>
        <div className="flex w-full border-b border-gray-200 justify-around pb-4">
          <span className="font-bold">30日間</span>
          <span className="font-bold">90日間</span>
          <span className="text-[#0909FF] font-bold">無制限</span>
        </div>
        <div className="text-center mt-4 mb-2">画像の詳細設定<div className="text-xs">※3</div></div>
        <div className="flex w-full border-b border-gray-200 justify-around pb-4 mb-4">
          <td><img src="/images/icon-close.svg" alt="x" className="h-[16px] mx-auto" /></td>
          <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
          <span><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></span>
        </div>
      </div>

      <div className="text-sm text-[#868E96] mb-20 mx-8 text-left sm:text-center">
        <div>※1 生成時の広告表示のON / OFF をコン トロールできます。マイページで設定可能です。</div>
        <div>※2 保存期限を経過した画像は閲覧履歴から削除されます。</div>
        <div>※3 シード値、ステップ数、サンプリングステップ数を調整できます。</div>
      </div>
      <div className="text-2xl font-bold text-primary">生成回数を追加で購入する</div>
      <div className="mt-4 mx-8 text-gray-500">1日の生成回数制限を解除して画像生成を楽しみたい方向け。基本機能が全て利用可能です。</div>
      <table className="w-[300px] sm:w-[800px] mx-auto mt-12 mb-12">
        <thead>
          <tr>
            <th>生成回数</th>
            <th>価格（税込）</th>
          </tr>
        </thead>
        <tbody>
          { stripeProducts.map((product) => (
            <tr className="border-b border-gray-200">
              <td className="py-4">{product.generationCount} 回</td>
              <td className="font-bold">¥{product.price.toLocaleString('ja-JP')}</td>
            </tr>
          ))
          }
        </tbody>
      </table>
      {isLoggedIn === true &&
      <div className="flex justify-center mt-4 mb-20">
        <button onClick={onClickCreditPurchase} className="bg-[#0909FF] text-white px-4 py-2 rounded-md w-[300px] font-bold">追加クレジット購入に進む</button>
      </div>
      }
      {isLoggedIn === false &&
      <>
      <div className="flex justify-center mt-4">
        <button onClick={onClickLogin} className="gradation-button text-white px-4 py-2 rounded-md w-[300px]">ログイン</button>
      </div>
      <div className="flex justify-center mt-6 mb-20">
        <a onClick={onClickRegisterLink} className="underline cursor-pointer text-[#0909FF]">アカウントをお持ちではない方はこちら</a>
      </div>
      </>
      }
    </div>
  </>;
}